import { useCallback, useEffect, useState } from 'react';
import { Form, Formik } from 'formik';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import FormFooter from '../../components/form-footer';
import IntakeGeneralInformationFields from '../../components/intake/general-information-fields';
import GeneralInformationSchema from '../../validation-schemas/general-information-schema';
import { updateActiveFormGeneralInformation, selectActiveFormGeneralInformation, selectActiveFormExistingOrder, updateActiveFormExistingOrder } from '../../reducers/active-form-slice';
import { selectPortalUser } from '../../reducers/auth-slice';
import { fetchOrderById, selectOrder, resetStatus} from '../../reducers/order-details-slice';
import { PrepopulateIntakeForms } from '../../utility/prepopulate-intake-forms';
import FormHeaderButtons from './form-header-buttons';
import { BeatLoader } from 'react-spinners';

const IntakeGeneralInformation = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    //if there is caseName query string present user was directed from case details "additional order", grab case name and portalUser info
    const [queryStringParams, setQueryStringParams] = useSearchParams();
    const caseName = queryStringParams.get('caseName');
    const portalUser = useSelector(selectPortalUser);

    //split the case name from the DB into three sections, Fist Name: cut off at the first space, Number: Everything after the last space, Last name: everything in between the two
    const parts = caseName ? caseName.split(' ') : [];
    const caseNameFirst = parts[0];
    const caseNumber = parts.length > 2 ? parts.pop() : "";
    const caseNameLast = parts.slice(1).join(' ');
    // return the three fields as an array for intitialValues
    const caseNameParts = [caseNameFirst, caseNameLast, caseNumber];

    // if OrderId query string is present, order is a draft
    const orderId = queryStringParams.get('orderId');
    const order = useSelector(selectOrder);
    const orderStatus = useSelector(state => state.orderDetails.status);
    const [orderDetailsLoaded, setOrderDetailsLoaded] = useState(false);
    const isExistingOrder = useSelector(selectActiveFormExistingOrder);
    const { populateFormData } = PrepopulateIntakeForms();

    // fetch order details on each initial render
    useEffect(() => {
        if (orderStatus === 'idle' && !orderDetailsLoaded && orderId != null) {
            dispatch(fetchOrderById(orderId));
        } else if (orderStatus === 'succeeded' && !orderDetailsLoaded) {
            // anything that needs to happen AFTER the data has loaded
            setOrderDetailsLoaded(true);
            dispatch(updateActiveFormExistingOrder(true));
            populateFormData();
        }
    }, [order, dispatch, orderId, orderStatus, orderDetailsLoaded]);

    useEffect(() => {
        if (orderStatus === 'succeeded' && orderDetailsLoaded) {
            dispatch(resetStatus());
        }
    }, [orderStatus, orderDetailsLoaded]);

    /**
     * get current form values
     */
    const generalInformation = useSelector(selectActiveFormGeneralInformation);
    const hasValues = Object.keys(generalInformation).length > 0 ? true : false;
    //?Don't think this should get called in any scenario.
    if (hasValues && orderId === null) {
        generalInformation.firstNameClient = caseNameParts[0] || '';
        generalInformation.lastNameClient = caseNameParts[1] || '';
        generalInformation.fileNumberClient = caseNameParts[2] || '';
    }

    
    /**
     * update active form & continue, if caseName we are passing the param to prepopulate first, last, and number on intake screen
     */
    const handleSubmit = (values) => {
        dispatch(updateActiveFormGeneralInformation(values));
        if(caseName) {
            navigate(`/intake?caseName=${caseName}`)
        } else {
        navigate('/intake');
        }
    };


    /**
     * setup the initial values, check to see if user is not a glucroft user and the caseName param is present
     */
    const isPortalUser = caseName && portalUser.userType !== "glucroft";

    const userValues = {
        firstNameCaseManager: isPortalUser ? portalUser.firstName : '',
        lastNameCaseManager: isPortalUser ? portalUser.lastName : '',
        emailPrimary: isPortalUser ? portalUser.email : '',
        emailSecondary: '',
        phonePrimary: isPortalUser ? portalUser.phone : '',
        phoneSecondary: '',
    };
    
    const firmValues = {
        lawFirmName: isPortalUser ? portalUser.organization?.firm : '',
        address: isPortalUser ? portalUser.organization?.lawFirmAddress1 : '',
        address2: isPortalUser ? portalUser.organization?.lawFirmAddress2 : '',
        city: isPortalUser ? portalUser.organization?.lawFirmCity : '',
        stateProvince: isPortalUser ? portalUser.organization?.lawFirmState : '',
        zip: isPortalUser ? portalUser.organization?.lawFirmZip : '',
        country:  isPortalUser ? portalUser.organization?.lawFirmCountry : '',
    };

    const clientInfoValues = {
        firstNameClient: caseNameParts[0] || '',
        lastNameClient: caseNameParts[1] || '',
        fileNumberClient: caseNameParts[2] || ''
    };

    const initialValues = {
        ...userValues,
        ...firmValues,
        ...clientInfoValues
    };    


    return (
        <div className="page intake">
            {(orderStatus === 'loading') &&
                <div className="loader-container">
                    <BeatLoader color="#00818C" />
                </div>
            }
            <Formik
                initialValues={hasValues ? generalInformation : initialValues}
                validationSchema={GeneralInformationSchema.fields.generalInformation}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ errors, dirty, isValid, touched, values }) => (
                    <Form>
                        <div className="form-header-buttons">
                            <div>
                                <h2>General Information</h2>
                                <p>FILL WITH “N/A” IF NOT APPLICABLE ON REQUIRED FIELDS</p>
                            </div>
                            <FormHeaderButtons updateForm={null} showSaveForLater={false}/>
                        </div>
                        
                        <IntakeGeneralInformationFields
                            errors={errors}
                            touched={touched}
                            values={values}
                            disabled={isExistingOrder}
                        />

                        <FormFooter
                            showPrevious={false}
                            disableNext={!((dirty || hasValues || caseName) && isValid)}
                            showNext={true}
                            progress={`5`}
                        />
                    </Form>
                )}
            </Formik>
        </div>
    );
};

export default IntakeGeneralInformation;